<script setup lang="ts">
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { computed } from 'vue';

  const props = defineProps<{
    price: number;
    discountedPrice?: number | null;
    hasOptions?: boolean;
    size?: 'sm' | 'md';
    hideDiscountBadge?: boolean;
    dense?: boolean;
  }>();

  const restaurantStore = useRestaurantStore();
  const restaurantHelper = useRestaurant();

  const currencySymbol = computed(() => restaurantStore.currency?.symbol);

  const hasDiscount = computed(
    () =>
      typeof props.discountedPrice !== 'undefined' &&
      props.discountedPrice !== null &&
      props.discountedPrice !== props.price
  );

  const finalPrices = computed(() => {
    const prices = {
      price: restaurantHelper.applyTax({
        price: props.price,
        partial: true,
      }),
      discountedPrice: hasDiscount.value
        ? restaurantHelper.applyTax({
            price: props.discountedPrice!,
            partial: true,
          })
        : null,
    };

    return prices;
  });

  function priceString(price: number): string {
    const priceText =
      displayPrice(price, restaurantStore.restaurantSettings?.showDecimals) +
      '';
    //if (!restaurantStore.restaurantSettings?.hideSymbol)
    return `${currencySymbol.value} ${priceText}`;
    //else return priceText;
  }

  const discountPercentage = computed(() => {
    if (!hasDiscount.value) return 0;

    return Math.floor(
      100 - (finalPrices.value.discountedPrice! * 100) / finalPrices.value.price
    );
  });
</script>

<template>
  <div
    class="price"
    :class="{ 'has-discount': hasDiscount, [`size-${size ?? 'sm'}`]: true }"
  >
    <span v-if="hasOptions && !dense" class="options-note">desde</span>
    <div class="inner">
      <template v-if="!hasDiscount">
        <span class="price-value">{{ priceString(finalPrices.price) }}</span>
      </template>
      <template v-else>
        <span class="price-value">{{
          priceString(finalPrices.discountedPrice)
        }}</span>
        <span v-if="!dense" class="old-price">{{
          priceString(finalPrices.price)
        }}</span>
        <v-chip
          v-if="!hideDiscountBadge && discountPercentage > 0"
          class="discount-badge"
          x-small
          label
          color="primary"
        >
          -{{ discountPercentage }}%
        </v-chip>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .price {
    @apply tw-flex tw-flex-col tw-whitespace-nowrap;

    .options-note {
      @apply tw-text-xs tw-text-gray-500;
    }

    .inner {
      @apply tw-flex tw-items-center tw-space-x-1;

      .price-value {
        @apply tw-font-normal tw-text-sm;
      }

      .old-price {
        @apply tw-text-xs tw-line-through tw-text-gray-500;
      }
    }

    &.size-md {
      .options-note {
        @apply tw-text-sm;
      }

      .inner {
        .price-value {
          @apply tw-text-base;
        }

        .old-price {
          @apply tw-text-sm;
        }
      }
    }
  }
</style>
